import React from 'react';
import { Collapse, InputAdornment, InputBase } from '@material-ui/core';
import { makeStyles, fade } from '@material-ui/core/styles';

import ShowCustomizedEndAdornment from './ShowCustomizedEndAdornment';

const Input = React.forwardRef(
  (
    {
      type = 'text',
      helperText = '',
      error = false,
      touched = false,
      errorContainerClassName = '',
      renderEndAdornment = ({ errorAndSuccessComponents }) => (
        <InputAdornment position="end">
          {errorAndSuccessComponents}
        </InputAdornment>
      ),
      containerClassName = '',
      ...props
    },
    ref,
  ) => {
    const { classes: propsClasses = {} } = props;
    const classes = useStyles();

    const rootClassNames = [classes.InputBaseRoot];
    const focusedClassNames = [classes.InputBaseRootFocused];
    const errorClassNames = [classes.HelperTextDiv];

    if (error) {
      focusedClassNames.push(classes.InputBaseRootErrorFocused);
      errorClassNames.push(classes.ErrorDiv);
    } else if (touched) {
      rootClassNames.push(classes.InputBaseRootSuccess);
      focusedClassNames.push(classes.InputBaseRootSuccessFocused);
    }

    const passwordType = type === 'password' ? { type: 'password' } : {};

    return (
      <div className={[classes.InputContainer, containerClassName].join(' ')}>
        <InputBase
          inputMode={type}
          {...passwordType}
          inputRef={ref}
          {...props}
          error={error}
          classes={{
            ...propsClasses,
            root: [...rootClassNames, propsClasses.root].join(' '),
            input: [
              classes.Input,
              props?.startAdornment ? classes.InputWithStartAdornment : '',
              propsClasses.input,
            ].join(' '),
            focused: [...focusedClassNames, propsClasses.focused].join(' '),
            error: [classes.InputBaseRootError, propsClasses.error].join(' '),
          }}
          endAdornment={
            <ShowCustomizedEndAdornment
              error={error}
              touched={touched}
              render={renderEndAdornment}
            />
          }
        />
        <Collapse in={!!helperText}>
          <div
            className={[...errorClassNames, errorContainerClassName].join(' ')}
          >
            {helperText}
          </div>
        </Collapse>
      </div>
    );
  },
);

export default Input;
export { ShowCustomizedEndAdornment };

const useStyles = makeStyles((theme) => ({
  InputContainer: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  InputBaseRoot: {
    width: '100%',
    height: '50px',
    background: '#f0f0f0',
    color: '#000',
    borderRadius: '10px',
    border: '1px solid transparent',
    boxShadow: '0 0 0 4px transparent',
    overflow: 'hidden',
    transition: 'all 0.3s',

    '& > div': {
      padding: '0 0.8rem',
    },
  },
  InputBaseRootFocused: {
    background: '#fefefe',
    border: `1px solid ${theme.palette.secondary.main}`,
    boxShadow: `0 0 0 4px ${fade(theme.palette.secondary.main, 0.3)}`,
  },
  InputBaseRootError: {
    background: fade(theme.palette.error.main, 0.2),
    color: '#000',
  },
  InputBaseRootErrorFocused: {
    border: `1px solid ${theme.palette.error.main}`,
    boxShadow: `0 0 0 4px ${fade(theme.palette.error.main, 0.3)}`,
    background: '#fefefe',
  },
  InputBaseRootSuccess: {
    background: fade(theme.palette.success.main, 0.2),
    color: '#000',
  },
  InputBaseRootSuccessFocused: {
    border: `1px solid ${theme.palette.success.main}`,
    boxShadow: `0 0 0 4px ${fade(theme.palette.success.main, 0.3)}`,
    background: '#fefefe',
  },
  Input: {
    width: '100%',
    height: '100%',
    padding: '0px 20px',
    transition: 'all 0.3s',
  },
  InputWithStartAdornment: {
    padding: '0 20px 0 0',
  },
  HelperTextDiv: {
    fontSize: theme.typography.pxToRem(13),
    margin: theme.spacing(0.5, 0, 0, 2.5),
  },
  ErrorDiv: {
    color: theme.palette.error.main,
  },
}));
